/**
 * 86-point pressure sensor array coordinate definitions.
 *
 * Spec: PO-5.1 — 滑雪智能鞋垫逐弯复盘网站
 *
 * This file defines the static layout for ONE foot (left). The right foot uses
 * the same layout with the Y axis mirrored at runtime (Y' = -Y).
 *
 * Coverage area : 270mm × 100mm
 * Origin        : geometric center of the insole
 * X axis        : points toward the toe  (range -135 to 135 mm)
 * Y axis        : points to the right side of the foot (range -50 to 50 mm)
 *
 * Regions:
 *   1. Forefoot (前掌): indices 0-39,  40 points, 5 rows × 8 cols,  X ∈ [42, 135],  Y ∈ [-50, 50]
 *   2. Arch    (足弓): indices 40-65, 26 points, 2 rows × 13 cols, X ∈ [-20, 42],  Y ∈ [-35, 35]
 *   3. Heel    (足跟): indices 66-85, 20 points, 4 rows × 5 cols,  X ∈ [-135, -20], Y ∈ [-40, 40]
 *
 * Note: a runtime `PressurePoint` (with a `value` field) is defined separately in
 * `src/types/ski-data.ts`. This module only describes the static geometry.
 */

/** Static coordinate of a single pressure sensor. */
export interface PressurePointCoordinate {
  index: number; // 0-85
  x: number; // mm, -135 to 135 (toward toe)
  y: number; // mm, -50 to 50 (toward right side of foot)
}

type Coordinate = { index: number; x: number; y: number };

/** Inclusive index ranges for each anatomical region. */
export const PRESSURE_REGION_RANGES = {
  forefoot: { start: 0, end: 39 },
  arch: { start: 40, end: 65 },
  heel: { start: 66, end: 85 },
} as const;

/** Total number of pressure points per shoe. */
export const PRESSURE_POINT_COUNT = 86 as const;

/** Coverage bounds of the insole, in millimetres. */
export const PRESSURE_INSOLE_BOUNDS = {
  xMin: -135,
  xMax: 135,
  yMin: -50,
  yMax: 50,
} as const;

/** Round to 1 decimal place. */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Generate the 40 forefoot points (indices 0-39) in a 5 × 8 grid.
 * x = 42 + col * (93 / 7)   for col 0..7
 * y = -50 + row * (100 / 4) for row 0..4
 * index = row * 8 + col
 */
function generateForefootPoints(): Coordinate[] {
  const points: Coordinate[] = [];
  const rows = 5;
  const cols = 8;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const index = row * cols + col;
      const x = round1(42 + col * (93 / 7));
      const y = round1(-50 + row * (100 / 4));
      points.push({ index, x, y });
    }
  }
  return points;
}

/**
 * Generate the 26 arch points (indices 40-65) in a 2 × 13 grid.
 * x = -20 + col * (62 / 12) for col 0..12
 * y = -35 + row * 70        for row 0..1  (i.e. -35 and 35)
 * index = 40 + row * 13 + col
 */
function generateArchPoints(): Coordinate[] {
  const points: Coordinate[] = [];
  const rows = 2;
  const cols = 13;
  const startIndex = 40;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const index = startIndex + row * cols + col;
      const x = round1(-20 + col * (62 / 12));
      const y = round1(-35 + row * 70);
      points.push({ index, x, y });
    }
  }
  return points;
}

/**
 * Generate the 20 heel points (indices 66-85) in a 4 × 5 grid.
 * x = -135 + col * (115 / 4) for col 0..4
 * y = -40 + row * (80 / 3)   for row 0..3
 * index = 66 + row * 5 + col
 */
function generateHeelPoints(): Coordinate[] {
  const points: Coordinate[] = [];
  const rows = 4;
  const cols = 5;
  const startIndex = 66;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const index = startIndex + row * cols + col;
      const x = round1(-135 + col * (115 / 4));
      const y = round1(-40 + row * (80 / 3));
      points.push({ index, x, y });
    }
  }
  return points;
}

/**
 * The full 86-point pressure sensor array for one foot.
 * Indices 0-39: forefoot, 40-65: arch, 66-85: heel.
 */
export const PRESSURE_POINT_COORDINATES: ReadonlyArray<{
  index: number;
  x: number;
  y: number;
}> = [
  ...generateForefootPoints(),
  ...generateArchPoints(),
  ...generateHeelPoints(),
];

/**
 * Returns the pressure point coordinate for the given index.
 * @throws {RangeError} if `index` is not an integer in [0, 85].
 */
export function getPressurePointByIndex(index: number): PressurePointCoordinate {
  if (
    !Number.isInteger(index) ||
    index < 0 ||
    index >= PRESSURE_POINT_COORDINATES.length
  ) {
    throw new RangeError(
      `Pressure point index out of range: ${index}. Expected 0..${
        PRESSURE_POINT_COORDINATES.length - 1
      }.`,
    );
  }
  return PRESSURE_POINT_COORDINATES[index];
}

/** Returns the 40 forefoot points (indices 0-39). */
export function getForefootPoints(): ReadonlyArray<PressurePointCoordinate> {
  return PRESSURE_POINT_COORDINATES.slice(
    PRESSURE_REGION_RANGES.forefoot.start,
    PRESSURE_REGION_RANGES.forefoot.end + 1,
  );
}

/** Returns the 26 arch points (indices 40-65). */
export function getArchPoints(): ReadonlyArray<PressurePointCoordinate> {
  return PRESSURE_POINT_COORDINATES.slice(
    PRESSURE_REGION_RANGES.arch.start,
    PRESSURE_REGION_RANGES.arch.end + 1,
  );
}

/** Returns the 20 heel points (indices 66-85). */
export function getHeelPoints(): ReadonlyArray<PressurePointCoordinate> {
  return PRESSURE_POINT_COORDINATES.slice(
    PRESSURE_REGION_RANGES.heel.start,
    PRESSURE_REGION_RANGES.heel.end + 1,
  );
}

/** Returns all 86 pressure point coordinates. */
export function getAllPoints(): ReadonlyArray<PressurePointCoordinate> {
  return PRESSURE_POINT_COORDINATES;
}
